import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, Observable, of, zip } from 'rxjs';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { StandardTrainingCategoryOverviewModel } from 'src/app/models/standard-training-category/standard-training-category-overview.model';
import { StandardTrainingCategoryTypeModel } from 'src/app/models/standard-training-category/standard-training-category-type.model';
import { StandardTrainingCategoryModel } from 'src/app/models/standard-training-category/standard-training-category.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { AdminAuthenticationService } from 'src/app/services/admin-authentication.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { StandardTrainingCategoryAdminService } from 'src/app/services/training-categories/standard-training-category-admin.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-standard-categories-admin-overview',
  standalone: false,
  templateUrl: './standard-categories-overview.component.html',
  styleUrl: './standard-categories-overview.component.scss',
})
export class StandardCategoriesOverviewComponent {
  isAuthenticated: boolean = false;
  isLoaded: boolean = false;
  selectedStandardCategoryIds: string[] = [];

  readonly stateKey = 'standard-training-categories-overview';

  standardTrainingCategories: StandardTrainingCategoryOverviewModel[] = [];
  
  standardTrainingCategoryColumns: TableColumn[] = [
    {
      caption: '',
      dataField: 'attention',
      cssClass: 'width-40',
      disableFiltering: true,
      cellTemplate: 'standardCategoryAttentionTemplate',
      cellValue: (category: StandardTrainingCategoryModel) => {
        return !category.imageFile;
      } 
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'type',
      cellValue: (category: StandardTrainingCategoryModel) => {
        switch (category.type) {
          case StandardTrainingCategoryTypeModel.PrivateInsurance:
            return this.translateService.instant(
              'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.PRIVATE-INSURANCES'
            );

          case StandardTrainingCategoryTypeModel.CorporateInsurance:
            return this.translateService.instant(
              'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.CORPORATE-INSURANCES'
            );

          default:
            return '';
        }
      } 
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.PARENT'),
      dataField: 'parent',
      cellValue: (category: StandardTrainingCategoryModel) => {
        if (!category.parentId) {
          return '';
        }

        const parent = this.standardTrainingCategories
          .find(x => x.id === category.parentId);

        return parent?.name;
      } 
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (
            category: StandardTrainingCategoryModel,
            e: any
          ): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();

            this.standardTrainingCategoryNavigateHandlerNewTab(
              category.id
            );
          }
        }
      ]
    }
  ];

  constructor(
    private adminAuthenticationService: AdminAuthenticationService,
    private standardTrainingCategoryService: StandardTrainingCategoryAdminService,
    private translateService: TranslateService,
    private router: Router,
    private loadingService: LoadingService,
    private pageStateService: PageStateService,
    private toastrService: ToastrService,
    private userContext: UserContextService
  ) {}

  ngOnInit(): void {
    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreStandardTrainingCategoriesOverview
    )) {
      this.pageStateService.clear(this.stateKey);
    }

    this.checkAuthentication();
  }

  private checkAuthentication(): void {
    this.loadCategories();
  }

  onLoginSuccess(): void {
    this.isAuthenticated = true;
    this.loadCategories();
  }

  private loadCategories(): void {
    this.loadingService
      .load(
        this.standardTrainingCategoryService
          .getStandardTrainingCategoriesOverview(
            this.userContext.currentPersonCulture ?? 'en'
          )
      )
      .pipe(
        finalize(() => this.isLoaded = true)
      )
      .subscribe({
        next: (standardTrainingCategories) => {
          this.standardTrainingCategories = standardTrainingCategories;
          this.isAuthenticated = true;
        },
        error: () => {
          this.isAuthenticated = false;
        }
      });
  }

  standardTrainingCategoryNavigateHandlerNewTab(id: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/standard-categories',
        id
      ])
    );

    window.open(url, '_blank');
  }

  standardCategoryNavigateHandler(id: string): void {
    this.router.navigate([
      '/standard-categories',
      id
    ]);
  }

  newStandardCategoryNavigateHandler() {
    this.router.navigate([
      '/standard-categories',
      'new'
    ]);
  }

  onStandardCategorySelected(ids: string[]): void {
    this.selectedStandardCategoryIds = ids;
  }

  deleteSelectedStandardCategories(): void {
    if (!this.selectedStandardCategoryIds.length) {
      return;
    }

    this.loadingService
      .load(
        zip(
          this.selectedStandardCategoryIds.map((id) =>
            this.standardTrainingCategoryService.deleteStandardTrainingCategory(id)
          )
        )
      )
      .subscribe(() => {
        this.toastrService.success(
          this.translateService.instant(
            'STANDARD-CATEGORIES.ADMIN.DELETED-STANDARD-CATEGORIES'
          )
        );
        this.selectedStandardCategoryIds = [];
        this.loadCategories();
      });
  }
}
